import { Fragment, type HTMLAttributes } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { usePathname } from 'next/navigation'
import Link from '@/components/ui/Link'
import PageSelect, { type CollectionTypes } from '@/components/ui/PageSelect'
import { Icon } from '@/icons/Icon'
import { cn } from '@/lib/utils'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import type { App } from '@/payload-types'
import type { LinkProps } from '@/types/app'

const breadcrumb = cva(
  'w-fit flex items-center gap-2.5 md:gap-3.5 text-gray-300 dark:text-white [&>span]:text-13 md:[&>span]:text-14',
  {
    variants: {
      theme: {
        light: '[&>span]:text-gray-300 dark:[&>span]:text-gray-500',
        dark: '[&>span]:text-white/40',
      },
      center: {
        true: 'mx-auto sm:mx-0',
        false: '',
      },
    },
  },
)

const links = cva(
  'group inline-flex items-center text-12 md:text-14 whitespace-nowrap transition-colors last:font-medium',
  {
    variants: {
      theme: {
        light: 'text-gray-500 hover:text-gray-950 last:text-gray-950 last:dark:text-white',
        dark: 'text-white/50 last:text-white hover:text-white',
      },
      hidden: {
        true: 'hidden sm:inline-flex',
        false: 'inline-flex',
      },
      gap: {
        true: 'gap-1.5',
        false: 'gap-0',
      },
    },
    defaultVariants: {
      gap: true,
    },
  },
)

type ComparisonLabel = Array<{ text: string; url: string }>

type BreadcrumbLinkProps = Omit<LinkProps, 'label'> & {
  label: string | ComparisonLabel
  isComparison?: boolean
}

const generateTrailFromPath = (
  pathname: string,
  parent?: BreadcrumbLinkProps,
  apps?: Pick<App, 'name' | 'slug'>[],
): BreadcrumbLinkProps[] => {
  const segments = pathname.split('/').filter(Boolean)
  const root = segments[0]
  const isSelectPath =
    root === 'apps' ||
    root === 'best' ||
    root === 'alternatives' ||
    root === 'deals' ||
    root === 'stacks'

  let trail = segments.map((segment, index) => {
    let url = `/${segments.slice(0, index + 1).join('/')}`

    // Handle comparison paths (containing 'vs')
    if (segment.includes('-vs-')) {
      const urlApps = segment.split('-vs-')
      if (urlApps.length > 1) {
        const label = urlApps.map((urlSlug) => {
          let appName = urlSlug
            .split('-')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')

          if (apps) {
            const matchedName = apps.find((app) => app.slug === urlSlug)
            if (matchedName) {
              appName = matchedName.name
            }
          }

          return {
            text: appName,
            url: `/apps/${urlSlug}`,
          }
        })
        return {
          label,
          url,
          isComparison: true,
        }
      }
    }

    // Handle normal paths
    const trail = segment
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
    const label =
      isSelectPath && index === segments.length - 1
        ? 'Select'
        : isSelectPath && trail === 'Best'
          ? `${trail} apps`
          : trail

    return { label, url, isComparison: false } as BreadcrumbLinkProps
  })

  if (parent && segments.length > 1) {
    trail.splice(1, 0, {
      label: parent.label,
      url: parent.url ?? '/',
      isComparison: false,
    } as BreadcrumbLinkProps)
  }

  return trail.filter(Boolean) as BreadcrumbLinkProps[]
}

type BreadcrumbProps = HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof breadcrumb> &
  VariantProps<typeof links> & {
    trail?: BreadcrumbLinkProps[]
    parent?: BreadcrumbLinkProps
    current?: string
    selectedLabel?: string
    center?: boolean
    apps?: Pick<App, 'name' | 'slug'>[]
  }

export default function Breadcrumb({
  trail,
  parent,
  current,
  selectedLabel,
  center,
  apps,
  theme = 'light',
  className,
  ...props
}: BreadcrumbProps) {
  const pathname = usePathname()
  const { sm } = useBreakpoint()
  const convertedPathname = current ? `${pathname}/${current}` : pathname
  const breadcrumbTrail = trail || generateTrailFromPath(convertedPathname, parent, apps)
  const isCompare = breadcrumbTrail.some((item) => item.isComparison)
  const isBlog = pathname === '/blog' || pathname.startsWith('/blog/')

  const retainMobile = isCompare || isBlog
  const CompareTag = !sm ? 'div' : 'h1'

  return (
    <div className={cn(breadcrumb({ theme, center }), className)} {...props}>
      {breadcrumbTrail.map((item, index) => {
        const isLast = index === breadcrumbTrail.length - 1

        return (
          <Fragment key={index}>
            {item.label === 'Select' ? (
              <PageSelect
                type={(breadcrumbTrail[0].url?.replace('/', '') ?? '') as CollectionTypes}
                selectedLabel={selectedLabel}
                center={center}
              />
            ) : item.isComparison ? (
              <CompareTag
                className={cn(
                  links({ theme, gap: false }),
                  'hidden font-default text-13 text-gray-300 dark:text-white sm:flex md:text-14',
                )}
              >
                {Array.isArray(item.label)
                  ? item.label.map((app, appIndex, array) => (
                      <Fragment key={app.url}>
                        <Link
                          href={app.url}
                          className="font-medium text-white/50 transition-colors duration-300 hover:text-white"
                        >
                          {app.text}
                        </Link>
                        {appIndex < array.length - 1 && (
                          <span className="text-white/50">&nbsp;vs&nbsp;</span>
                        )}
                      </Fragment>
                    ))
                  : item.label}
              </CompareTag>
            ) : isLast && breadcrumbTrail.length > 1 ? (
              <span className={links({ theme })}>
                {item.icon && <Icon name={item.icon} size={12} className="text-gray-500" />}
                {item.label as string}
              </span>
            ) : (
              <Link href={item.url ?? ''} className={links({ theme, hidden: !retainMobile })}>
                {item.icon && (
                  <Icon
                    name={item.icon}
                    className="text-gray-500 transition-colors duration-300 group-hover:text-primary-500"
                  />
                )}
                {item.label as string}
              </Link>
            )}
            {index < breadcrumbTrail.length - 1 && (
              <span
                className={
                  isCompare && index === breadcrumbTrail.length - 2
                    ? 'hidden sm:flex'
                    : retainMobile
                      ? 'flex'
                      : 'hidden sm:flex'
                }
              >
                /
              </span>
            )}
          </Fragment>
        )
      })}
    </div>
  )
}
